import type { LockerverseAuction as Auction } from "@lockerverse/sdk/auctions";
import { ArrowRight, Gavel, ListChecks } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  CatalogImage,
  Metric,
  STATUS_LABELS,
  Timing,
} from "./auction-catalog-parts.tsx";
import { AuctionDescription } from "./auction-description.tsx";
import { getAuctionDisplayStatus } from "./auction-display.ts";
import {
  AuctionContent,
  type LockerverseAuctionProps,
} from "./lockerverse-auction.tsx";
import { useAuctionBoundary } from "./use-auction-boundary.ts";
import type { UseLockerverseAuctionsResult } from "./use-lockerverse-auctions.ts";

export type LockerverseAuctionsProps = Omit<
  LockerverseAuctionProps,
  "auction" | "onBack"
> & {
  auctions: UseLockerverseAuctionsResult;
  getAuctionHref?: (auction: Auction) => string;
  onSelectAuction?: (auction: Auction) => void;
};

function AuctionCard({
  auction,
  onSelect,
  href,
}: {
  auction: Auction;
  onSelect?: () => void;
  href?: string;
}) {
  useAuctionBoundary(auction.startAt, auction.endAt, auction.items);
  const fallback =
    auction.configuration.image ||
    auction.items.find((item) => item.image || item.images.length)?.image ||
    auction.items.find((item) => item.images.length)?.images[0] ||
    auction.community?.banner ||
    auction.community?.thumbnail;
  const description =
    auction.description ||
    {
      active: "Bidding is open on available items.",
      draft: "Preview the lineup before bidding opens.",
      finished: "Bidding has wrapped. Review the completed items.",
    }[getAuctionDisplayStatus(auction)];
  return (
    <article className="lockerverse-auctions__catalog-card">
      {href ? (
        <a
          aria-label={`View items in ${auction.name}`}
          className="lockerverse-auctions__catalog-select"
          data-auction-id={auction.id}
          href={href}
          onClick={(event) => {
            if (
              onSelect &&
              event.button === 0 &&
              !event.metaKey &&
              !event.ctrlKey &&
              !event.shiftKey &&
              !event.altKey
            ) {
              event.preventDefault();
              onSelect();
            }
          }}
        >
          <span className="lockerverse-auctions__visually-hidden">
            View items in {auction.name}
          </span>
        </a>
      ) : (
        <button
          aria-label={`View items in ${auction.name}`}
          className="lockerverse-auctions__catalog-select"
          data-auction-id={auction.id}
          onClick={onSelect}
          type="button"
        />
      )}
      <div className="lockerverse-auctions__catalog-media">
        <CatalogImage fallback={fallback} name={auction.name} />
        <div className="lockerverse-auctions__catalog-chips">
          <span className="lockerverse-auctions__catalog-chip">
            {STATUS_LABELS[getAuctionDisplayStatus(auction)]}
          </span>
          <Timing auction={auction} />
        </div>
        <dl className="lockerverse-auctions__catalog-metrics">
          <Metric
            icon={<ListChecks aria-hidden="true" size={16} />}
            label="Items"
            value={String(auction.items.length)}
          />
          <Metric
            icon={<Gavel aria-hidden="true" size={16} />}
            label="Bids"
            value={String(
              auction.items.reduce((sum, item) => sum + item.bidCount, 0)
            )}
          />
        </dl>
      </div>
      <div className="lockerverse-auctions__catalog-body">
        <h3>{auction.name}</h3>
        <AuctionDescription
          className="lockerverse-auctions__catalog-description"
          description={description}
        />
        <div className="lockerverse-auctions__catalog-footer">
          <span className="lockerverse-auctions__catalog-cta">
            {getAuctionDisplayStatus(auction) === "draft"
              ? "Preview items"
              : "View items"}
          </span>
          <span className="lockerverse-auctions__catalog-arrow">
            <ArrowRight aria-hidden="true" size={16} />
          </span>
        </div>
      </div>
    </article>
  );
}

/** Optional auction browser. Select an auction to show its items. */
export function LockerverseAuctions({
  auctions,
  theme = "dark",
  style,
  className,
  showFilters = true,
  showHeader = true,
  showLineup = false,
  getAuctionHref,
  onSelectAuction,
  ...contentProps
}: LockerverseAuctionsProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const previousId = useRef(selectedId);
  const selected = auctions.auctions?.find(
    (auction) => auction.id === selectedId
  );
  useEffect(() => {
    if (previousId.current !== selectedId) {
      if (selectedId === null) {
        const card = [
          ...(sectionRef.current?.querySelectorAll<HTMLButtonElement>(
            "button[data-auction-id]"
          ) ?? []),
        ].find((button) => button.dataset.auctionId === previousId.current);
        (headingRef.current ?? card ?? sectionRef.current)?.focus();
      }
      previousId.current = selectedId;
    }
  }, [selectedId]);
  return (
    <section
      aria-busy={auctions.loading}
      aria-label="Auctions"
      className={[
        "lockerverse-auctions",
        "lockerverse-auctions--catalog",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      data-lockerverse-theme={theme}
      ref={sectionRef}
      style={style}
      tabIndex={-1}
    >
      {!selected && showHeader ? (
        <header className="lockerverse-auctions__catalog-header">
          <h2 ref={headingRef} tabIndex={-1}>
            Auctions
          </h2>
        </header>
      ) : null}
      {auctions.loading ? (
        <p role="status">
          {auctions.auctions ? "Refreshing auctions…" : "Loading auctions…"}
        </p>
      ) : null}
      {auctions.error ? (
        <div role="alert">
          <p>{auctions.error.message}</p>
          <button onClick={auctions.refresh} type="button">
            Try again
          </button>
        </div>
      ) : null}
      {selected ? (
        <AuctionContent
          auction={selected}
          key={selected.id}
          onBack={() => setSelectedId(null)}
          {...contentProps}
          showFilters={showFilters}
          showHeader={showHeader}
          showLineup={showLineup}
          theme={theme}
          {...(style ? { style } : {})}
        />
      ) : (
        <AuctionList
          auctions={auctions.auctions}
          onSelectAuction={(auction) => {
            if (onSelectAuction) {
              onSelectAuction(auction);
            } else {
              setSelectedId(auction.id);
            }
          }}
          {...(getAuctionHref ? { getAuctionHref } : {})}
          interceptLinks={Boolean(onSelectAuction)}
          showLineup={showLineup}
        />
      )}
    </section>
  );
}

function AuctionList({
  auctions,
  showLineup,
  onSelectAuction,
  getAuctionHref,
  interceptLinks,
}: {
  auctions: Auction[] | null;
  showLineup: boolean;
  onSelectAuction: (auction: Auction) => void;
  getAuctionHref?: (auction: Auction) => string;
  interceptLinks: boolean;
}) {
  if (!auctions) {
    return null;
  }
  if (!auctions.length) {
    return (
      <div className="lockerverse-auctions__catalog-empty">
        <h3>No auctions yet.</h3>
        <p>Published auctions will appear here once they are available.</p>
      </div>
    );
  }
  return (
    <>
      {showLineup ? (
        <div className="lockerverse-auctions__catalog-lineup">
          <div>
            <h3>Auction lineup</h3>
            <p>Browse the current collection.</p>
          </div>
          <span>
            {auctions.length} {auctions.length === 1 ? "auction" : "auctions"}
          </span>
        </div>
      ) : null}
      <ul className="lockerverse-auctions__catalog-grid">
        {auctions.map((auction) => (
          <li key={auction.id}>
            <AuctionCard
              auction={auction}
              {...(getAuctionHref ? { href: getAuctionHref(auction) } : {})}
              {...(!getAuctionHref || interceptLinks
                ? { onSelect: () => onSelectAuction(auction) }
                : {})}
            />
          </li>
        ))}
      </ul>
    </>
  );
}
